/**
 * The armed-status strip. A quiet little row of pills on the chart HUD that says,
 * at a glance, which safety/nav modes are currently ON — so a mode is never
 * silently armed (anchor watch running, alarms set) or silently off. It sits
 * top-left, under the overlays button, clear of the top-right data boxes, the
 * bottom-right MOB/controls and the left-edge brightness tab.
 *
 * Everything here is a shell overlay: it reads the existing stores and opens the
 * existing sheets/menus. It touches no map code. When a MOB is live the whole
 * top of the screen belongs to the red MOB readout, so the strip drops below it
 * (the same shift Chrome uses) rather than fighting it.
 *
 * The chips only show for things that are actually on, and the strip renders
 * nothing at all when the derivation comes back empty, so at rest it isn't there.
 *
 * Store reads use single-value selectors (booleans, ids, counts) and useMemo over
 * stable slices, never a fresh array/object out of a selector, so it stays clear
 * of the zustand re-render loop.
 */
import { useMemo } from 'react';
import { useSafetyStore } from '@/store/safety';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { useRoutingStore } from '@/store/routing';
import { useAlarmsStore } from '@/store/alarms';
import { useShellStore } from '@/store/shell';
import { haptic } from '@/ui/haptics';
import { deriveArmedChips, countArmedAlarms, type ArmedChipId } from './armedChips';

export function ArmedStrip() {
  // --- Live state, one primitive per selector so references stay stable. ---
  const mobActive = useSafetyStore((s) => s.mob != null);
  const anchorArmed = useAnchorWatchStore((s) => s.anchor != null);
  // The drag alarm is published live into the alarms store (computed in MapView),
  // the same feed the banner and siren read, so the chip agrees with them.
  const anchorDragging = useAlarmsStore((s) => s.live.anchor != null);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const routes = useRoutingStore((s) => s.routes);
  const alarmsConfig = useAlarmsStore((s) => s.config);

  const openSheet = useShellStore((s) => s.openSheet);
  const openMenuAt = useShellStore((s) => s.openMenuAt);

  const routeName = useMemo(() => {
    if (!followRouteId) return null;
    const r = routes.find((r) => r.id === followRouteId);
    return r?.name?.trim() || null;
  }, [followRouteId, routes]);

  const armedCount = useMemo(() => countArmedAlarms(alarmsConfig), [alarmsConfig]);

  const chips = useMemo(
    () =>
      deriveArmedChips({
        mob: { active: mobActive },
        anchor: { armed: anchorArmed, dragging: anchorDragging },
        routeFollow: { following: followRouteId != null, routeName },
        alarms: { armedCount },
      }),
    [mobActive, anchorArmed, anchorDragging, followRouteId, routeName, armedCount],
  );

  if (!chips.length) return null;

  // Tap a chip to jump to the control behind it. All existing sheets/menus.
  const openFor = (id: ArmedChipId) => {
    haptic('select');
    switch (id) {
      case 'mob':
        openSheet('mobsearch');
        break;
      case 'anchor':
        openMenuAt('safety', 'anchor-watch');
        break;
      case 'alarms':
        openSheet('alarms');
        break;
      case 'follow':
        openSheet('route');
        break;
    }
  };

  return (
    <div
      className={`acp-armed-strip${mobActive ? ' mob-shift' : ''}`}
      role="status"
      aria-label="Armed modes"
    >
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`acp-armed-chip tone-${c.tone}`}
          title={c.title}
          aria-label={c.title}
          onClick={() => openFor(c.id)}
        >
          <span className="ic" aria-hidden="true">
            {c.icon}
          </span>
          <span className="lbl">{c.label}</span>
        </button>
      ))}
    </div>
  );
}
